/*
 * Copyright 2026 Sony Corporation
 */
import { CssBaseline, ThemeProvider } from '@mui/material';
import type { ReactElement } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { AppShell } from './components/AppShell';
import type { AppConfig } from './config';
import { AuthProvider } from './contexts/AuthProvider';
import { useAuth } from './contexts/auth-context';
import { Admin } from './pages/Admin';
import { Dashboard } from './pages/Dashboard';
import { Login } from './pages/Login';
import { ProjectDetail } from './pages/ProjectDetail';
import { Projects } from './pages/Projects';
import { Runtimes } from './pages/Runtimes';
import { theme } from './theme';

const RequireAuth = ({ children }: { children: ReactElement }) => {
  const { signedIn } = useAuth();
  const location = useLocation();
  return signedIn ? children : <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
};

export const AppRoutes = () => (
  <Routes>
    <Route path="/login" element={<Login />} />
    <Route
      element={
        <RequireAuth>
          <AppShell />
        </RequireAuth>
      }
    >
      <Route path="/projects" element={<Projects />} />
      <Route path="/projects/:projectId" element={<ProjectDetail />} />
      <Route path="/runtimes" element={<Runtimes />} />
      <Route path="/devices" element={<Navigate to="/runtimes" replace />} />
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/admin" element={<Admin />} />
    </Route>
    <Route path="*" element={<Navigate to="/projects" replace />} />
  </Routes>
);

export const App = ({ config }: { config: AppConfig }) => (
  <ThemeProvider theme={theme}>
    <CssBaseline />
    <AuthProvider config={config}>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  </ThemeProvider>
);
